import { useId, useState, type ComponentPropsWithRef } from 'react';

import { Icon } from '@/components/ui/icon';
import { CONTROL_BASE } from '@/components/ui/input';
import { cn } from '@/lib/cn';

export interface PasswordInputProps extends Omit<ComponentPropsWithRef<'input'>, 'type'> {
  invalid?: boolean;
  /** Hides the reveal control - for a field that must never be shown. */
  hideToggle?: boolean;
  /** Renders a strength meter under the field. For new passwords only. */
  showStrength?: boolean;
}

/**
 * Password field with a reveal toggle.
 *
 * Letting people check what they typed measurably reduces failed sign-ins,
 * especially on the generated `Abcadmin@123` credentials, which are easy to
 * mistype. It starts hidden and never persists the revealed state.
 */
export function PasswordInput({
  invalid = false,
  hideToggle = false,
  showStrength = false,
  className,
  value,
  ...props
}: PasswordInputProps) {
  const [revealed, setRevealed] = useState(false);
  const strengthId = useId();

  const text = typeof value === 'string' ? value : '';

  return (
    <div>
      <div className="relative">
        <input
          type={revealed ? 'text' : 'password'}
          value={value}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={showStrength ? strengthId : props['aria-describedby']}
          className={cn(
            CONTROL_BASE,
            'h-9 px-2 text-xs sm:h-7',
            !hideToggle && 'pr-7',
            invalid ? 'border-danger' : 'border-border-strong hover:border-content-subtle',
            className,
          )}
          {...props}
        />

        {!hideToggle && (
          <button
            type="button"
            /* Not a tab stop: a keyboard user typing a password should land on
               the next field, not on a reveal button. Still reachable by
               pointer, and by screen readers through the form controls list. */
            tabIndex={-1}
            onClick={() => {
              setRevealed((previous) => !previous);
            }}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded-sm p-0.5 text-content-subtle transition-colors hover:text-content"
          >
            <Icon name={revealed ? 'eye-off' : 'eye'} size={14} />
          </button>
        )}
      </div>

      {showStrength && text.length > 0 && <StrengthMeter id={strengthId} password={text} />}
    </div>
  );
}

const STRENGTH_LABELS = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'] as const;
const STRENGTH_COLOURS = ['bg-danger', 'bg-danger', 'bg-warning', 'bg-accent', 'bg-success'];

/**
 * A rough strength hint, not a security control - the server enforces the
 * actual rules. It scores the same four requirements the API checks, plus
 * length, so the bar and the checklist never disagree.
 */
function scorePassword(password: string): number {
  let score = 0;

  if (password.length >= 8) {
    score += 1;
  }
  if (password.length >= 12) {
    score += 1;
  }
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
    score += 1;
  }
  if (/\d/.test(password)) {
    score += 1;
  }
  if (/[^A-Za-z0-9]/.test(password)) {
    score += 1;
  }

  return Math.min(score, 4);
}

function StrengthMeter({ id, password }: { id: string; password: string }) {
  const score = scorePassword(password);

  return (
    <div className="mt-1.5">
      <div className="flex gap-1" aria-hidden="true">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className={cn(
              'h-0.5 flex-1 rounded-full transition-colors',
              index <= score ? STRENGTH_COLOURS[score] : 'bg-border',
            )}
          />
        ))}
      </div>

      <p id={id} className="mt-1 text-2xs text-content-subtle">
        Strength: {STRENGTH_LABELS[score]}
      </p>
    </div>
  );
}
